import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Heart } from "lucide-react";
import Petals from "../common/Petals";
import SplitText from "../common/SplitText";
import { fonts, palette } from "../../theme";

const EASE = [0.16, 1, 0.3, 1] as const;

// ── Données (mise en page fixe ; textes dans src/i18n.ts) ─────

interface StoryItem {
  id: number;
  date: string;
  month?: string;
  titleKey: string;
  textKey: string;
  image: string;
  side: "left" | "right";
}

const stories: StoryItem[] = [
  { id: 1, date: "2019", titleKey: "story.item1Title", textKey: "story.item1Text", image: "/assets/photo1.jpeg", side: "left" },
  { id: 2, date: "28", month: "08.23", titleKey: "story.item2Title", textKey: "story.item2Text", image: "/assets/photo3.jpeg", side: "right" },
  { id: 3, date: "2024", titleKey: "story.item3Title", textKey: "story.item3Text", image: "/assets/photo4.jpeg", side: "left" },
  { id: 4, date: "2026", titleKey: "story.item4Title", textKey: "story.item4Text", image: "/assets/photo29.jpeg", side: "right" },
];

// ── Styled ────────────────────────────────────────────────────

const Section = styled("section")({
  position: "relative",
  overflow: "hidden",
  padding: "110px 20px 140px",
  background:
    "radial-gradient(120% 75% at 50% 0%, #FCF8F2 0%, #F6EEE1 55%, #F1E7D6 100%)",
  "@media (max-width:600px)": { padding: "80px 14px 110px" },
});

// Voile dégradé vers le blanc pour fondre dans la section suivante
const Mist = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 140,
  background: "linear-gradient(to bottom, rgba(255,255,255,0), #ffffff)",
  pointerEvents: "none",
  zIndex: 1,
});

const Header = styled(motion.div)({
  position: "relative",
  zIndex: 2,
  textAlign: "center",
  maxWidth: 680,
  margin: "0 auto 80px",
});

const HeaderTitle = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "clamp(2rem, 5vw, 3rem)",
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: palette.ink,
});

const Subtitle = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1.06rem",
  lineHeight: 1.95,
  color: palette.warmGray,
  whiteSpace: "pre-line",
  marginTop: "20px",
});

const Timeline = styled(Box)({
  position: "relative",
  zIndex: 2,
  maxWidth: 980,
  margin: "0 auto",
});

const Track = styled(Box)({
  position: "absolute",
  top: 0,
  bottom: 0,
  left: "calc(50% - 1px)",
  width: 2,
  background: "rgba(191,160,106,0.18)",
  "@media (max-width:820px)": { left: 21 },
});

const Progress = styled(motion.div)({
  position: "absolute",
  top: 0,
  left: "calc(50% - 1px)",
  width: 2,
  height: "100%",
  transformOrigin: "top",
  background: "linear-gradient(180deg, #D9BE86, #A8884F)",
  boxShadow: "0 0 12px rgba(191,160,106,0.6)",
  "@media (max-width:820px)": { left: 21 },
});

const Item = styled(Box, { shouldForwardProp: (p) => p !== "side" })<{
  side: "left" | "right";
}>(({ side }) => ({
  position: "relative",
  display: "grid",
  alignItems: "center",
  columnGap: "30px",
  gridTemplateColumns: "1fr 88px 1fr",
  gridTemplateAreas: side === "left" ? '"img dot card"' : '"card dot img"',
  marginBottom: "96px",
  "&:last-of-type": { marginBottom: 0 },
  "@media (max-width:820px)": {
    gridTemplateColumns: "44px 1fr",
    gridTemplateAreas: '"dot img" "dot card"',
    columnGap: "18px",
    rowGap: "16px",
    alignItems: "start",
    marginBottom: "64px",
  },
}));

const DotCell = styled(Box)({
  gridArea: "dot",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "@media (max-width:820px)": { alignItems: "flex-start", paddingTop: "4px" },
});

const Dot = styled(motion.div)({
  position: "relative",
  width: 74,
  height: 74,
  borderRadius: "50%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background:
    "radial-gradient(circle at 35% 28%, #F0DCB4, #BFA06A 62%, #A8884F)",
  color: "#fff",
  border: "2px solid rgba(255,255,255,0.75)",
  boxShadow: "0 8px 24px rgba(168,136,79,0.42)",
  zIndex: 2,
  transition:
    "transform .45s cubic-bezier(.16,1,.3,1), box-shadow .45s ease",
  "&:hover": {
    transform: "scale(1.08)",
    boxShadow: "0 12px 32px rgba(168,136,79,0.6)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "2px solid rgba(191,160,106,0.55)",
    animation: "haloPulse 3.2s ease-out infinite",
  },
  "@media (max-width:820px)": { width: 56, height: 56 },
});

const DotYear = styled("span")({
  fontFamily: fonts.display,
  fontSize: "1.15rem",
  fontWeight: 600,
  lineHeight: 1,
  "@media (max-width:820px)": { fontSize: "0.95rem" },
});

const DotMonth = styled("span")({
  fontFamily: fonts.sans,
  fontSize: "0.5rem",
  letterSpacing: "0.12em",
  opacity: 0.92,
  marginTop: "3px",
});

const ImageCell = styled(Box)({ gridArea: "img" });

const ImageFrame = styled(motion.div)({
  borderRadius: 18,
  overflow: "hidden",
  aspectRatio: "4 / 3",
  border: "1px solid rgba(191,160,106,0.28)",
  boxShadow: "0 18px 46px rgba(58,53,47,0.20)",
  transition: "box-shadow .5s ease",
  "&:hover": { boxShadow: "0 26px 60px rgba(58,53,47,0.28)" },
  "&:hover img": { transform: "scale(1.05)" },
});

const StoryImg = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  animation: "kenburns 18s ease-in-out infinite alternate",
  transition: "transform .6s cubic-bezier(.16,1,.3,1)",
});

const CardCell = styled(Box)({ gridArea: "card" });

const Card = styled(motion.div)({
  background: "rgba(252,248,242,0.55)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid rgba(191,160,106,0.35)",
  borderRadius: 18,
  boxShadow: "0 12px 34px rgba(58,53,47,0.10)",
  padding: "24px 26px",
  transition:
    "transform .5s cubic-bezier(.16,1,.3,1), box-shadow .5s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 24px 50px rgba(58,53,47,0.16)",
  },
});

const CardTitle = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "1.3rem",
  color: palette.ink,
  marginBottom: "10px",
  letterSpacing: "0.02em",
});

const CardText = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1.04rem",
  lineHeight: 1.85,
  color: palette.warmGray,
});

const Footer = styled(Box)({
  position: "relative",
  zIndex: 2,
  textAlign: "center",
  marginTop: "70px",
});

const FooterText = styled(Typography)({
  fontFamily: fonts.script,
  fontSize: "1.8rem",
  color: palette.goldDeep,
});

// Divider floral discret
function Flourish() {
  return (
    <Box aria-hidden sx={{ display: "flex", justifyContent: "center", my: "16px" }}>
      <svg width="220" height="16" viewBox="0 0 220 16" fill="none">
        <path d="M4 8 H92" stroke="#BFA06A" strokeWidth="1" />
        <path d="M128 8 H216" stroke="#BFA06A" strokeWidth="1" />
        <path d="M100 8 q5 -7 10 0 q-5 7 -10 0Z" fill="#BFA06A" />
        <circle cx="110" cy="8" r="2.4" fill="#BFA06A" />
        <circle cx="95" cy="8" r="1.5" fill="#BFA06A" />
        <circle cx="125" cy="8" r="1.5" fill="#BFA06A" />
      </svg>
    </Box>
  );
}

// ── Un événement (hooks par item pour la parallaxe) ───────────

function TimelineItem({ item }: { item: StoryItem }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yMove = useTransform(scrollYProgress, [0, 1], [26, -26]);
  const title = t(item.titleKey);
  const text = t(item.textKey);

  return (
    <Item ref={ref} side={item.side}>
      <DotCell>
        <Dot
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <DotYear>{item.date}</DotYear>
          {item.month && <DotMonth>{item.month}</DotMonth>}
        </Dot>
      </DotCell>

      <ImageCell>
        <motion.div style={{ y: reduce ? 0 : yMove }}>
          <ImageFrame
            initial={{ opacity: 0, filter: "blur(16px)", scale: 1.08 }}
            whileInView={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, ease: EASE }}
          >
            <StoryImg src={item.image} alt={title} loading="lazy" />
          </ImageFrame>
        </motion.div>
      </ImageCell>

      <CardCell>
        <Card
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <CardTitle>
            <SplitText text={title} stagger={0.02} />
          </CardTitle>
          <CardText>{text}</CardText>
        </Card>
      </CardCell>
    </Item>
  );
}

// ── Composant ─────────────────────────────────────────────────

export default function OurStory() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const timelineRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  return (
    <Section>
      <Petals count={9} opacity={0.4} />

      {/* En-tête premium */}
      <Header
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <Box
          sx={{
            display: "inline-flex",
            color: palette.gold,
            mb: "10px",
            animation: "heartbeat 2.8s ease-in-out infinite",
          }}
        >
          <Heart size={26} fill={palette.gold} strokeWidth={1} />
        </Box>

        <HeaderTitle>
          <SplitText
            text={`${t("story.headerPre")} ${t("story.headerPost")}`}
            stagger={0.05}
          />
        </HeaderTitle>

        {/* Soulignement doré qui se dessine */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          whileInView={{ scaleX: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
          style={{
            height: 2,
            width: 130,
            margin: "16px auto 0",
            background:
              "linear-gradient(90deg, transparent, #BFA06A, transparent)",
            transformOrigin: "center",
          }}
        />

        <Subtitle>{t("story.subtext")}</Subtitle>
      </Header>

      <Flourish />

      {/* Timeline */}
      <Timeline ref={timelineRef}>
        <Track />
        <Progress style={{ scaleY: reduce ? 1 : scrollYProgress }} />
        {stories.map((item) => (
          <TimelineItem key={item.id} item={item} />
        ))}
      </Timeline>

      <Footer>
        <Flourish />
        <FooterText>{t("story.footer")}</FooterText>
      </Footer>

      <Mist />
    </Section>
  );
}
